import { useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, ImagePlus, Undo2, Redo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function RichTextEditor({ value, onChange, testId = "rte" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [StarterKit, Image],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  const btn = (active) =>
    `inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
      active ? "bg-[#0B1F3A] text-white" : "text-[#172033]/70 hover:bg-[#0B1F3A]/10"
    }`;

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image is too large (max 5 MB).");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/image", fd);
      editor?.chain().focus().setImage({ src: data.url }).run();
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const tools = [
    { icon: Bold, label: "Bold", active: editor?.isActive("bold"), run: () => editor?.chain().focus().toggleBold().run() },
    { icon: Italic, label: "Italic", active: editor?.isActive("italic"), run: () => editor?.chain().focus().toggleItalic().run() },
    { icon: Heading2, label: "Heading", active: editor?.isActive("heading", { level: 2 }), run: () => editor?.chain().focus().toggleHeading({ level: 2 }).run() },
    { icon: Heading3, label: "Subheading", active: editor?.isActive("heading", { level: 3 }), run: () => editor?.chain().focus().toggleHeading({ level: 3 }).run() },
    { icon: List, label: "Bullet list", active: editor?.isActive("bulletList"), run: () => editor?.chain().focus().toggleBulletList().run() },
    { icon: ListOrdered, label: "Numbered list", active: editor?.isActive("orderedList"), run: () => editor?.chain().focus().toggleOrderedList().run() },
    { icon: Quote, label: "Quote", active: editor?.isActive("blockquote"), run: () => editor?.chain().focus().toggleBlockquote().run() },
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-[#0B1F3A]/15 bg-white focus-within:border-[#A87B1F]" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-[#0B1F3A]/10 bg-[#F5F8FC] px-2 py-1.5">
        {tools.map((t) => (
          <button key={t.label} type="button" onClick={t.run} className={btn(t.active)} aria-label={t.label} title={t.label} data-testid={`${testId}-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <t.icon size={15} />
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-[#0B1F3A]/10" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className={btn(false)} aria-label="Insert image" title="Insert image from your computer" data-testid={`${testId}-insert-image`}>
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid={`${testId}-image-file-input`} />
        <span className="mx-1 h-5 w-px bg-[#0B1F3A]/10" />
        <button type="button" onClick={() => editor?.chain().focus().undo().run()} className={btn(false)} aria-label="Undo" title="Undo" data-testid={`${testId}-undo`}>
          <Undo2 size={15} />
        </button>
        <button type="button" onClick={() => editor?.chain().focus().redo().run()} className={btn(false)} aria-label="Redo" title="Redo" data-testid={`${testId}-redo`}>
          <Redo2 size={15} />
        </button>
      </div>
      <EditorContent editor={editor} className="rte-content" data-testid={`${testId}-content`} />
    </div>
  );
}
